import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { updateProviderProfile } from '../services/providers';
import { NotificationType } from '@/types/modelEnums';
import logger from '@/layers/support/monitoring/logger';
import { ProviderProfile } from '@/types/models';

const LOG_MODULE = '[Hooks / useProviderProfileUpdate]';

interface ProfileUpdateData {
  npi: string;
  acknowledgement: boolean;
}

export const useProviderProfileUpdate = (providerProfile: ProviderProfile | null) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const { refreshProfile } = useAuth();
  const { handleNotification } = useNotification();

  const updateProfile = async (data: ProfileUpdateData): Promise<void> => {
    if (!providerProfile) return;

    setIsUpdating(true);
    try {
      logger.debug(`${LOG_MODULE} Updating provider profile`, { data });

      await updateProviderProfile(providerProfile.id, {
        meta: {
          ...providerProfile.meta,
          npi: data.npi,
          acknowledgement: data.acknowledgement,
        },
      });

      await refreshProfile();

      handleNotification(
        NotificationType.Success,
        'Your provider profile has been successfully updated.',
      );
    } catch (error) {
      logger.error(`${LOG_MODULE} Error updating provider profile`, { error });
      handleNotification(NotificationType.Error, 'Failed to update profile. Please try again.');
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const getInitialValues = () => {
    if (!providerProfile) {
      return {
        npi: '',
        acknowledgement: false,
      };
    }

    return {
      npi: providerProfile.meta?.npi || providerProfile.npi || '',
      acknowledgement: providerProfile.meta?.acknowledgement || false,
    };
  };

  return {
    updateProfile,
    isUpdating,
    getInitialValues,
  };
};
